import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

/** One governed code (D-020) as a picker needs it. */
export interface ReferenceOption {
  kind: string;
  code: string;
  /** In the viewer's language. */
  name: string;
  names?: Record<string, string>;
  aliases?: string[];
  attributes?: Record<string, unknown>;
  enabled: boolean;
  favourite?: boolean;
}

export interface ReferenceSearchOptions {
  limit?: number;
  includeDisabled?: boolean;
  /** Narrows by the `country` attribute (subdivisions, locales). */
  parent?: string;
  signal?: AbortSignal;
}

/**
 * Where reference codes come from. The app provides one backed by `/platform/refdata` (tenant-resolved, localised,
 * cached); tests and the playground provide an in-memory one.
 */
export interface ReferenceSource {
  search(kind: string, query: string, options?: ReferenceSearchOptions): Promise<ReferenceOption[]>;
  get(kind: string, code: string, signal?: AbortSignal): Promise<ReferenceOption | null>;
  /** A code already known without a request (the source's cache), so a stored value renders before any load. */
  peek?(kind: string, code: string): ReferenceOption | undefined;
}

const ReferenceSourceContext = createContext<ReferenceSource | null>(null);

export function ReferenceProvider({ source, children }: { source: ReferenceSource; children: ReactNode }) {
  return <ReferenceSourceContext.Provider value={source}>{children}</ReferenceSourceContext.Provider>;
}

export function useReferenceSource(): ReferenceSource | null {
  return useContext(ReferenceSourceContext);
}

/** One code, resolved through the source (peek first, then a request). `undefined` while it loads. */
export function useReferenceOption(kind: string, code: string | null | undefined): { option: ReferenceOption | null | undefined; loading: boolean } {
  const source = useReferenceSource();
  const peeked = code && source?.peek ? source.peek(kind, code) : undefined;
  const [state, setState] = useState<{ key: string; option: ReferenceOption | null } | null>(null);
  const key = `${kind}:${code ?? ''}`;
  useEffect(() => {
    if (!code || !source || peeked) return;
    const controller = new AbortController();
    source
      .get(kind, code, controller.signal)
      .then((option) => setState({ key, option }))
      .catch(() => {
        if (!controller.signal.aborted) setState({ key, option: null });
      });
    return () => controller.abort();
  }, [kind, code, source, peeked, key]);
  if (!code) return { option: null, loading: false };
  if (peeked) return { option: peeked, loading: false };
  if (state?.key === key) return { option: state.option, loading: false };
  return { option: undefined, loading: !!source };
}

/** The flag emoji of an ISO 3166-1 alpha-2 code (regional indicator symbols); empty for anything else. */
export function flagOf(countryCode: string | undefined | null): string {
  if (!countryCode || !/^[A-Za-z]{2}$/.test(countryCode)) return '';
  const base = 0x1f1e6;
  return String.fromCodePoint(...[...countryCode.toUpperCase()].map((c) => base + c.charCodeAt(0) - 65));
}

/** The small visual that goes before a code: a flag for countries/locales/subdivisions, a symbol for currencies. */
export function referenceMark(option: Pick<ReferenceOption, 'kind' | 'code' | 'attributes'>): string {
  const attrs = option.attributes ?? {};
  switch (option.kind) {
    case 'country':
      return flagOf(option.code);
    case 'locale':
    case 'subdivision':
      return flagOf(typeof attrs.country === 'string' ? attrs.country : undefined);
    case 'currency':
      return typeof attrs.symbol === 'string' && attrs.symbol !== option.code ? attrs.symbol : '';
    case 'unit':
      return typeof attrs.symbol === 'string' ? attrs.symbol : '';
    default:
      return '';
  }
}
